import { Button } from '@/components/ui/button';
import { GradientDialog } from '@/components/ui/gradient-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyRound, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordModal({
  open,
  onOpenChange,
}: ForgotPasswordModalProps) {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
  });

  const handleSubmit = async (data: ForgotPasswordFormValues) => {
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setEmailSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setEmailSent(false);
      setError('');
      form.reset();
    }, 200);
  };

  return (
    <AnimatePresence>
      {open && (
        <GradientDialog
          open={open}
          onOpenChange={handleClose}
          gradientFrom="from-purple-500/10"
          gradientTo="to-blue-500/5"
          borderColor="border-purple-500/20"
          contentClassName="flex flex-col items-center justify-center"
          maxWidth="sm"
        >
          {!emailSent ? (
            <div className="flex flex-col items-center justify-center py-6 space-y-6 w-full">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 20 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                }}
                className="relative w-24 h-24 mb-2"
              >
                <div className="absolute inset-0 bg-linear-to-br from-purple-400 to-blue-500 rounded-xl opacity-20 blur-xl" />
                <div className="relative flex items-center justify-center w-full h-full bg-linear-to-br from-purple-500/30 to-blue-500/30 backdrop-blur-xs rounded-xl border border-white/20">
                  <KeyRound className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-xl font-semibold text-center"
              >
                Reset your password
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-sm text-muted-foreground text-center"
              >
                Enter your email address and we'll send you a link to reset your
                password.
              </motion.p>

              {/* Form */}
              <motion.form
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                onSubmit={form.handleSubmit(handleSubmit)}
                className="w-full space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm font-medium">
                    Email address
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    className="h-11"
                    placeholder="name@company.com"
                    {...form.register('email')}
                    aria-invalid={form.formState.errors.email ? 'true' : 'false'}
                  />
                  {form.formState.errors.email && (
                    <p className="text-xs text-destructive mt-1">
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>

                {error && (
                  <p className="text-sm text-destructive text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 font-medium"
                  disabled={loading || !form.formState.isValid}
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </motion.form>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 space-y-6">
              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 20 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                }}
                className="relative w-24 h-24 mb-2"
              >
                <div className="absolute inset-0 bg-linear-to-br from-purple-400 to-blue-500 rounded-xl opacity-20 blur-xl" />
                <div className="relative flex items-center justify-center w-full h-full bg-linear-to-br from-purple-500/30 to-blue-500/30 backdrop-blur-xs rounded-xl border border-white/20">
                  <Mail className="w-12 h-12 text-white" />
                </div>
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-xl font-semibold text-center"
              >
                Check your email
              </motion.h2>

              {/* Message */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-center space-y-2"
              >
                <p className="text-muted-foreground">
                  We've sent a password reset link to:
                </p>
                <p className="font-medium text-primary">
                  {form.getValues('email')}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Click the link in the email to reset your password.
                </p>
              </motion.div>

              {/* Button */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="w-full mt-4"
              >
                <Button
                  onClick={handleClose}
                  className="w-full relative overflow-hidden group"
                >
                  <span className="relative z-10">Got it</span>
                  <motion.div
                    className="absolute inset-0 bg-linear-to-r from-purple-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={false}
                    animate={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  />
                </Button>
              </motion.div>

              {/* Hint */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="text-xs text-muted-foreground mt-4"
              >
                Didn't receive an email? Check your spam folder.
              </motion.p>
            </div>
          )}
        </GradientDialog>
      )}
    </AnimatePresence>
  );
}
